import {Alert, Box, CircularProgress, Grid, TextField, Typography} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
// Components
import { Formtitle, IperiaLayout } from '../../components'
// Hooks
import { useIperiaStatus } from '../../hooks/useIperiaStatus'
// Utils
import { IperiaFormDefaultState } from '../../utils/constants'
import { IperiaFormType } from '../../utils/sharedTypes'
import { getApiErrorMessage } from '../../utils/apiErrors'

type AddressFields = 'address' | 'address_line_2' | 'city' | 'postal_code' | 'country'

const VerificationAddress = () => {
  const navigate = useNavigate()
  const { global_id: globalId } = useParams<{ global_id: string }>()
  const { status, loading, error, updateSummary } = useIperiaStatus()
  const [form, setForm] = useState<IperiaFormType>(IperiaFormDefaultState)
  const [errors, setErrors] = useState<Partial<Record<AddressFields, string>>>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!status?.learner_summary) return

    setForm({
      ...IperiaFormDefaultState,
      data: {
        ...IperiaFormDefaultState.data,
        ...status.learner_summary
      }
    })
  }, [status?.learner_summary])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !status) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        Impossible de charger votre dossier.
      </Typography>
    )
  }

  const alreadyConfirmed = status.learner_summary_confirmed
  const formDisabled = alreadyConfirmed

  const handleChange = (key: AddressFields, value: string) => {
    setSubmitError(null)
    setErrors((prev) => {
      const next = { ...prev }
      delete next[key]

      const keyMapping: Record<string, string> = {
        address: 'Veuillez renseigner une adresse',
        city: 'Veuillez renseigner une ville',
        postal_code: 'Veuillez renseigner un code postal',
      }
      if (!value.trim() && keyMapping[key]) {
        next[key] = keyMapping[key]
      }
      return next
    })

    setForm((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        [key]: value,
      },
    }))
  }

  const validateForm = () => {
    const next: Partial<Record<AddressFields, string>> = {}
    if (!form.data.address.trim()) next.address = 'Veuillez renseigner une adresse'
    if (!form.data.city.trim()) next.city = 'Veuillez renseigner une ville'
    if (!form.data.postal_code.trim()) next.postal_code = 'Veuillez renseigner un code postal'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleNext = async () => {
    if (!globalId) return

    if (alreadyConfirmed) {
      navigate(`/iperia/${globalId}/verification/formation`)
      return
    }
    if (!validateForm()) return

    setSubmitting(true)
    setSubmitError(null)
    try {
      await updateSummary({
        address: form.data.address,
        address_line_2: form.data.address_line_2,
        city: form.data.city,
        postal_code: form.data.postal_code,
        country: form.data.country || 'France',
      })
      navigate(`/iperia/${globalId}/verification/formation`)
    } catch (err) {
      setSubmitError(getApiErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  const handleBack = () => {
    if (!globalId) return
    navigate(`/iperia/${globalId}/verification`)
  }

  return (
    <IperiaLayout
      productName={form.data.product_name}
      handleBack={handleBack}
      handleNext={handleNext}
      submitting={submitting}
      submittingNext={submitting}
      alreadyConfirmed={alreadyConfirmed}
    >
      <Formtitle
        title="Étape 2 : Votre adresse"
        subtitle="Veuillez renseigner votre adresse complète."
      />
      {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Adresse"
            inputProps={{ autoComplete: 'address-line1' }}
            id="address"
            required
            disabled={formDisabled}
            value={form.data.address}
            onChange={(e) => handleChange('address', e.target.value)}
            error={Boolean(errors.address)}
            helperText={errors.address}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Complément d'adresse"
            inputProps={{ autoComplete: 'address-line2' }}
            id="address_line_2"
            disabled={formDisabled}
            value={form.data.address_line_2}
            onChange={(e) => handleChange('address_line_2', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Code postal"
            inputProps={{ autoComplete: 'postal-code', inputMode: 'numeric', maxLength: 5 }}
            id="postal_code"
            required
            disabled={formDisabled}
            value={form.data.postal_code}
            onChange={(e) => handleChange('postal_code', e.target.value)}
            error={Boolean(errors.postal_code)}
            helperText={errors.postal_code}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Ville"
            inputProps={{ autoComplete: 'address-level2' }}
            id="city"
            required
            disabled={formDisabled}
            value={form.data.city}
            onChange={(e) => handleChange('city', e.target.value)}
            error={Boolean(errors.city)}
            helperText={errors.city}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Pays"
            inputProps={{ autoComplete: 'country-name' }}
            id="country"
            disabled={formDisabled}
            value={form.data.country || 'France'}
            onChange={(e) => handleChange('country', e.target.value)}
          />
        </Grid>
      </Grid>
    </IperiaLayout>
  )
}

export default VerificationAddress
